import type * as React from 'react'
import { useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import { Dialog, EmptyState, WarningIcon } from '@buffer-mono/popcorn'

import {
  ContentItemCard_ContentItem,
  ContentItemDetailsCard,
} from '~publish/components/ContentItemCard'
import { graphql, readFragment } from '~publish/graphql'
import { classifyPostFetchError } from '~publish/helpers/posts/classifyPostFetchError'
import { isOfType } from '~publish/helpers/typeGuards'
import { useContentItemNotesEnabled } from '~publish/hooks/notes/useContentItemNotesEnabled'

import { getReturnSearchOnDialogClose } from '../helpers'
import { DraftDetailsNotes } from './DraftDetailsNotes'

import styles from './ContentItemDetailsDialog.module.css'

import type { JSX } from 'react'

export const GetContentItemDetails = graphql(
  `
    query GetContentItemDetails($id: ContentItemId!) {
      contentItem(input: { id: $id }) {
        id
        ...ContentItemCard_ContentItem
      }
    }
  `,
  [ContentItemCard_ContentItem],
)

/**
 * The channel-less draft details dialog, mounted over the drafts list.
 * ContentItemPermalink only sends a draft here once it knows the body is still
 * `DraftContent`, so anything else means a stale hand-built URL, and reads as gone.
 *
 * With ContentItem Notes on, the draft's Notes sit beside it. Their summary
 * is its own query, never part of the draft's, so a failed summary cannot
 * turn a valid draft into an unavailable one.
 */
export function ContentItemDetailsDialog(): JSX.Element | null {
  const navigate = useNavigate()
  const location = useLocation()
  const { contentItemId } = useParams<{ contentItemId: string }>()

  const returnSearch = getReturnSearchOnDialogClose(location.search)
  const [open, setOpen] = useState(true)

  const { data, loading, error } = useQuery(GetContentItemDetails, {
    variables: { id: contentItemId ?? '' },
    skip: !contentItemId,
  })
  const notesEnabled = useContentItemNotesEnabled()
  // Once shown for a draft, Notes stay when it is scheduled mid-session, so
  // the closed thread keeps any unsent Note, as it does in the composer.
  const shownDraftIdRef = useRef<string | null>(null)

  const closeDialogWhenComposerOpens = (
    evt: React.MouseEvent<HTMLElement>,
  ): void => {
    if (
      (evt.target as Element | null)?.closest('[aria-controls="composer-root"]')
    ) {
      setOpen(false)
      navigate(`..${returnSearch}`)
    }
  }

  const onOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen)
    if (!nextOpen) {
      navigate(`..${returnSearch}`)
    }
  }

  if (loading && !data) {
    return null
  }

  const contentItem = readFragment(
    ContentItemCard_ContentItem,
    data?.contentItem,
  )
  const draft =
    contentItem && isOfType(contentItem.body, 'DraftContent')
      ? contentItem
      : null
  if (draft) shownDraftIdRef.current = draft.id
  const notesContentItemId =
    notesEnabled && shownDraftIdRef.current === contentItemId
      ? contentItemId
      : null

  const details = (notesAction?: React.ReactNode): React.ReactNode =>
    draft ? (
      <ContentItemDetailsCard
        contentItem={draft}
        hideExpand
        notesAction={notesAction}
      />
    ) : (
      <DraftNotAvailableState error={error} />
    )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        size="medium"
        className={styles.dialogContent}
        onClick={closeDialogWhenComposerOpens}
      >
        <Dialog.CloseButton />
        {notesContentItemId ? (
          <DraftDetailsNotes contentItemId={notesContentItemId}>
            {details}
          </DraftDetailsNotes>
        ) : (
          details()
        )}
      </Dialog.Content>
    </Dialog>
  )
}

const DraftNotAvailableState = ({
  error,
}: {
  error?: unknown
}): JSX.Element => {
  const failure = classifyPostFetchError(error)

  if (failure === 'accessDenied') {
    return (
      <EmptyState variant="warning">
        <EmptyState.Icon>
          <WarningIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Access restricted</EmptyState.Heading>
        <EmptyState.Description>
          You don&apos;t have access to this draft. Reach out to your
          organization owner to request access.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  if (failure === 'unavailable') {
    return (
      <EmptyState variant="warning">
        <EmptyState.Icon>
          <WarningIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Draft not found</EmptyState.Heading>
        <EmptyState.Description>
          This draft has been deleted, or given channels and turned into posts.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  return (
    <EmptyState variant="critical">
      <EmptyState.Icon>
        <WarningIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>Error happened</EmptyState.Heading>
      <EmptyState.Description>
        Error happened fetching the draft, please let our team know about it.
      </EmptyState.Description>
    </EmptyState>
  )
}
